import { useState, useRef, useEffect } from 'react';
import Multitrack from 'wavesurfer-multitrack';
import type { Stem } from '@/types/stem';

interface Track {
  id: number;
  name: string;
  url: string;
  isMuted: boolean;
  isSoloed: boolean;
  volume: number;
  color: string;
}

interface MultitrackInstance {
  play: () => void;
  pause: () => void;
  destroy: () => void;
  getDuration: () => number;
  setTrackVolume: (trackId: number, volume: number) => void;
  on: (event: string, callback: (arg: any) => void) => void;
}

interface TrackConfig {
  id: number;
  url: string;
  volume: number;
  startPosition: number;
  options: {
    waveColor: string;
    progressColor: string;
    height: number;
    barWidth: number;
    barGap: number;
    barRadius: number;
  };
}

export const useMultitrack = (stems: Stem[], originalUrl: string) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [masterVolume, setMasterVolume] = useState(1);
  const [tracks, setTracks] = useState<Track[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  
  const multitrackRef = useRef<MultitrackInstance | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const stemColors = [
    '#50A090', // Green
    '#66C6BF', // Teal/Green-Blue
    '#5D9AC8', // Blue
    '#4A74A0', // Dark Blue
    '#68669A', // Purple
    '#A074AA', // Magenta
    '#C28EBA', // Pink
  ];

  useEffect(() => {
    if (!containerRef.current || !stems.length) return;

    const initTracks = stems.map((stem, index) => ({
      id: index,
      name: stem.name,
      url: stem.url,
      isMuted: false,
      isSoloed: false,
      volume: 1,
      color: stemColors[index % stemColors.length],
    }));

    setTracks(initTracks);

    try {
      // Calculate minPxPerSec based on container width and audio duration
      const containerWidth = containerRef.current.clientWidth;
      const avgDuration = stems[0]?.duration || 180; // fallback to 3 minutes
      const minPxPerSec = containerWidth / avgDuration;

      const trackConfigs = initTracks.map(track => ({
        id: track.id,
        url: track.url,
        volume: track.volume,
        startPosition: 0,
        options: {
          waveColor: track.color,
          progressColor: `${track.color}80`,
          height: 48,
          barWidth: 2,
          barGap: 1,
          barRadius: 2,
        },
      })) as TrackConfig[];

      multitrackRef.current = (Multitrack.create(
        trackConfigs,
        {
          container: containerRef.current,
          minPxPerSec: minPxPerSec
        }
      ) as unknown) as MultitrackInstance;

      multitrackRef.current.on('loading', (progress: number) => {
        if (progress === 100) {
          setIsLoading(false);
        }
      });

      multitrackRef.current.on('timeupdate', (currentTime: number) => {
        setCurrentTime(currentTime);
      });

      multitrackRef.current.on('ready', () => {
        setDuration(multitrackRef.current?.getDuration() || 0);
      });

      multitrackRef.current.on('finish', () => {
        setIsPlaying(false);
      });

    } catch (error) {
      console.error('Error initializing multitrack:', error);
      setLoadError(error instanceof Error ? error.message : String(error));
    }

    return () => {
      multitrackRef.current?.destroy();
    };
  }, [stems, originalUrl]);

  const togglePlayPause = () => {
    if (multitrackRef.current) {
      if (isPlaying) {
        multitrackRef.current.pause();
      } else {
        multitrackRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const setTrackVolume = (trackId: number, volume: number) => {
    if (multitrackRef.current) {
      multitrackRef.current.setTrackVolume(trackId, volume);
      setTracks(tracks.map(track => 
        track.id === trackId ? { ...track, volume } : track
      ));
    }
  };

  const setTrackSolo = (trackId: number) => {
    if (multitrackRef.current) {
      const updatedTracks = tracks.map(track => ({
        ...track,
        isSoloed: track.id === trackId ? !track.isSoloed : track.isSoloed,
      }));

      const hasSoloedTracks = updatedTracks.some(track => track.isSoloed);
      updatedTracks.forEach(track => {
        const shouldBeMuted = hasSoloedTracks && !track.isSoloed;
        multitrackRef.current?.setTrackVolume(track.id, shouldBeMuted ? 0 : track.volume);
      });

      setTracks(updatedTracks);
    }
  };

  const setTrackMute = (trackId: number) => {
    if (multitrackRef.current) {
      const updatedTracks = tracks.map(track => 
        track.id === trackId ? { ...track, isMuted: !track.isMuted } : track
      );
      
      const track = updatedTracks.find(t => t.id === trackId);
      if (track) {
        multitrackRef.current.setTrackVolume(trackId, track.isMuted ? 0 : track.volume);
      }
      
      setTracks(updatedTracks);
    }
  };

  const setGlobalVolume = (volume: number) => {
    if (multitrackRef.current) {
      tracks.forEach(track => {
        if (!track.isMuted && (!tracks.some(t => t.isSoloed) || track.isSoloed)) {
          multitrackRef.current?.setTrackVolume(track.id, track.volume * volume);
        }
      });
      setMasterVolume(volume);
    }
  };

  return {
    containerRef,
    isPlaying,
    isLoading,
    currentTime,
    duration,
    masterVolume,
    tracks,
    loadError,
    togglePlayPause,
    setTrackVolume,
    setTrackSolo,
    setTrackMute,
    setGlobalVolume,
  };
};
